import type { ReactNode } from 'react'

import { Button } from '@jotta/ui/Button'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { cn } from '@jotta/utils/css'

import { type CollectionRef, useAlbumId } from '../../api/useCollection'
import { useSelectionStore } from '../../store/SelectionStore'

import type { ActionDispatcherProps } from './PhotoActions'
import { PhotoAction } from './PhotoActions'
import type { PhotoActionType } from './PhotoActions.types'

function Dispatcher({
  hasGhostButtons,
  onClick,
  loading,
  icon,
  label,
  testid,
}: ActionDispatcherProps & { hasGhostButtons: boolean }) {
  const md = useBreakpoint(Breakpoint.md)
  return (
    <Button
      className={cn('btn text-current', {
        'btn-tonal': !hasGhostButtons,
        'btn-ghost': hasGhostButtons,
      })}
      onClick={onClick}
      loading={loading}
      icon={icon}
      data-testid={testid}
    >
      {md && label}
    </Button>
  )
}

function OutlineDispatcher(props: ActionDispatcherProps) {
  return <Dispatcher hasGhostButtons={false} {...props} />
}

function GhostDispatcher(props: ActionDispatcherProps) {
  return <Dispatcher hasGhostButtons={true} {...props} />
}

/**
 * Hosts a selection bar's actions: hands its `PhotoAction` children the current selection
 * and the button style the bar it sits in is laid out for.
 *
 * The bar is a bottom status bar until the module nav has room to hold it, which is the
 * same breakpoint `SelectionManager` picks its slot by.
 */
export function PhotoSelectionActionBar({
  collectionRef,
  hasMd5OnlyPhotos,
  onTrackAction,
  children,
}: {
  collectionRef: CollectionRef
  /** See `PhotoAction.Provider`: set when the photos only identify themselves by md5. */
  hasMd5OnlyPhotos?: boolean
  onTrackAction?: (actionType: PhotoActionType) => void
  children?: ReactNode
}) {
  const hasGhostButtons = !useBreakpoint(Breakpoint.xl)
  const selectedPhotos = useSelectionStore().values

  return (
    <PhotoAction.Provider
      collectionRef={collectionRef}
      photos={selectedPhotos}
      hasMd5OnlyPhotos={hasMd5OnlyPhotos}
      onTrackAction={onTrackAction}
      actionDispatcherComponent={
        hasGhostButtons ? GhostDispatcher : OutlineDispatcher
      }
    >
      {children}
    </PhotoAction.Provider>
  )
}

/** The actions any collection of photos offers for a selection. */
export function PhotoSelectionActions({
  collectionRef,
}: {
  collectionRef: CollectionRef
}) {
  // Which destructive action a selection gets is the collection's answer, not the caller's:
  // photos come out of an album they were put in, but a collection with no album behind it
  // — a location album, the timeline — has nowhere to take them out of, so they delete.
  const albumId = useAlbumId(collectionRef)

  return (
    <PhotoSelectionActionBar collectionRef={collectionRef}>
      <PhotoAction.SharePhotos />
      <PhotoAction.AddToAlbum />
      <PhotoAction.HidePhotos />
      <PhotoAction.UnhidePhotos />
      {albumId ? <PhotoAction.RemoveFromAlbum /> : <PhotoAction.DeletePhotos />}
      <PhotoAction.DownloadPhotos />
    </PhotoSelectionActionBar>
  )
}
